/**
 * Input — pointer/touch drag hit-test + velocity tracker.
 *
 * The hot path during a drag:
 *   - onDown:  hit-test, capture node, set dragOffset, start tracking velocity
 *   - onMove:  update node position from pointer (suppresses node.update()
 *              so the engine sees a static prev → current delta = 0, no blur)
 *   - onUp:    hand the node the last few pointer deltas as a release velocity
 *
 * Hit-test:
 *   - Reverse z-order iteration.
 *   - First pass: bounding-box hit.
 *   - Second pass: SDF-aware hit when the node has a corner radius. The math
 *     mirrors the FS SDF but in JS — `q = abs(p) - b + r; d = length(max(q,0))
 *     + min(max(q.x,q.y),0) - r`. A point within `radius + touchSlop` of the
 *     SDF boundary is treated as a hit so rounded corners don't have a
 *     dead-zone relative to the bounding box.
 *
 * Inertia / release:
 *   - Velocity is the average of the last N dx/dy over their dt.
 *   - On pointerup, the engine's frame loop applies it via friction:
 *     `n.x += vx * dt; vy *= 0.92` until magnitude < 20 px/s.
 *   - Off-screen guard: if x or y leaves the viewport, snap back with a small
 *     spring (one-shot animation).
 */

import type { Scene, SceneNode, NodeId } from "./sceneGraph";
import { sortedNodes } from "./sceneGraph";

const VEL_WINDOW_MS = 60; // length of the velocity tracking window
const VEL_MIN_PX_PER_S = 20; // below this, no inertia
const FRICTION = 0.92; // per-frame decay
const TOUCH_SLOP = 6; // extra px around the SDF boundary for forgiving taps

interface PointerSample {
  dx: number;
  dy: number;
  t: number;
}

export interface PointerState {
  active: boolean;
  id: number | null;
  x: number;
  y: number;
  dx: number;
  dy: number;
  dragging: NodeId | null;
  /** Last few pointer movements, used to compute release velocity. */
  samples: PointerSample[];
  /** Release velocity in CSS px/s, set on pointerup if dragging. */
  releaseVx: number;
  releaseVy: number;
  /** Set on pointerup to flag that inertia is active. Cleared when stopped. */
  inertia: boolean;
  /** Per-node drag offset so dragging feels stable. */
  dragOffsetX: number;
  dragOffsetY: number;
  /** Last drag end position, used for off-screen snap-back. */
  releaseX: number;
  releaseY: number;
  /** Viewport size for snap-back bounds. */
  viewportW: number;
  viewportH: number;
}

export function createPointer(): PointerState {
  return {
    active: false,
    id: null,
    x: 0,
    y: 0,
    dx: 0,
    dy: 0,
    dragging: null,
    samples: [],
    releaseVx: 0,
    releaseVy: 0,
    inertia: false,
    dragOffsetX: 0,
    dragOffsetY: 0,
    releaseX: 0,
    releaseY: 0,
    viewportW: 0,
    viewportH: 0,
  };
}

export function setViewportSize(p: PointerState, w: number, h: number): void {
  p.viewportW = w;
  p.viewportH = h;
}

/**
 * SDF-aware hit-test. Returns topmost node under (x,y) or null.
 * Uses bounding-box first, then optional SDF check for rounded corners.
 */
export function hitTest(scene: Scene, x: number, y: number): SceneNode | null {
  const nodes = sortedNodes(scene);
  for (let i = nodes.length - 1; i >= 0; i--) {
    const n = nodes[i];
    if (n.opacity <= 0) continue;
    if (x < n.x || x > n.x + n.w) continue;
    if (y < n.y || y > n.y + n.h) continue;

    if (n.radii[0] > 0) {
      // Compute SDF distance in local frame.
      const lx = x - (n.x + n.w * 0.5);
      const ly = y - (n.y + n.h * 0.5);
      const rad = n.radii[0];
      const bx = Math.max(n.w * 0.5 - rad, 0.0001);
      const by = Math.max(n.h * 0.5 - rad, 0.0001);
      const qx = Math.abs(lx) - bx + rad;
      const qy = Math.abs(ly) - by + rad;
      const qxC = Math.max(qx, 0);
      const qyC = Math.max(qy, 0);
      const outside = Math.hypot(qxC, qyC);
      const inside = Math.min(Math.max(qx, qy), 0);
      const d = outside + inside - rad;
      // d<=0 = inside rounded rect. With touch slop we also accept pixels
      // just outside the SDF (so taps near the rounded edge count).
      if (d > TOUCH_SLOP) continue;
    }
    return n;
  }
  return null;
}

/**
 * Record a pointer movement sample. Maintains a rolling window of the last
 * VEL_WINDOW_MS.
 */
export function recordPointerSample(p: PointerState, dx: number, dy: number, t: number): void {
  p.samples.push({ dx, dy, t });
  const cutoff = t - VEL_WINDOW_MS;
  while (p.samples.length > 1 && p.samples[0].t < cutoff) {
    p.samples.shift();
  }
}

/**
 * Compute release velocity (px/s) from the rolling window. Returns (0,0) if
 * the window is empty or the velocity is below VEL_MIN_PX_PER_S.
 */
export function computeReleaseVelocity(p: PointerState): { vx: number; vy: number } {
  if (p.samples.length < 2) return { vx: 0, vy: 0 };
  const first = p.samples[0];
  const last = p.samples[p.samples.length - 1];
  const dt = Math.max((last.t - first.t) / 1000, 0.001);
  const sumDx = p.samples.reduce((s, x) => s + x.dx, 0);
  const sumDy = p.samples.reduce((s, x) => s + x.dy, 0);
  const vx = sumDx / dt;
  const vy = sumDy / dt;
  if (Math.hypot(vx, vy) < VEL_MIN_PX_PER_S) return { vx: 0, vy: 0 };
  return { vx, vy };
}

/**
 * Per-frame inertia update. Call from the RAF loop after update() nodes have
 * run. Returns true if inertia is still active (caller keeps watching).
 */
export function tickInertia(p: PointerState, dt: number): boolean {
  if (!p.inertia || p.dragging == null) return false;
  // The engine doesn't have a stable reference to the scene here; we just
  // decay the velocity and let the next frame's update apply it via the
  // velocity-attached state. Instead we mutate the node through the engine
  // via the public applyInertia helper below.
  // The pointer state doesn't actually carry the node — see `applyInertia()`
  // in main.ts.
  void dt;
  return true;
}

/**
 * Compute the new node position after one frame of inertia, applying:
 *   - velocity *= friction^frame
 *   - position += velocity * dt
 *   - bounce off viewport edges (with restitution 0.5)
 *   - stop when |velocity| < VEL_MIN_PX_PER_S
 *
 * Returns { x, y, vx, vy, done }. `done=true` when velocity is negligible
 * or the node is back inside the viewport and slow enough to stop.
 */
export interface InertiaStep {
  x: number;
  y: number;
  vx: number;
  vy: number;
  done: boolean;
}

const RESTITUTION = 0.5;
const MAX_STEP_PX = 200; // clamp per-frame delta to avoid tunneling

export function stepInertia(
  n: SceneNode,
  vx: number,
  vy: number,
  dt: number,
  viewportW: number,
  viewportH: number,
): InertiaStep {
  // Apply friction (FRICTION is per-60fps frame; dt/0.0166 is fraction-of-frame).
  const f = Math.pow(FRICTION, dt / 0.0166);
  vx *= f;
  vy *= f;

  let nx = n.x + Math.max(-MAX_STEP_PX, Math.min(MAX_STEP_PX, vx * dt));
  let ny = n.y + Math.max(-MAX_STEP_PX, Math.min(MAX_STEP_PX, vy * dt));

  // Bounce off viewport edges with restitution.
  if (nx < 0) {
    nx = -nx * RESTITUTION;
    vx = -vx * RESTITUTION;
  } else if (nx + n.w > viewportW) {
    nx = viewportW - n.w - (nx + n.w - viewportW) * RESTITUTION;
    vx = -vx * RESTITUTION;
  }
  if (ny < 0) {
    ny = -ny * RESTITUTION;
    vy = -vy * RESTITUTION;
  } else if (ny + n.h > viewportH) {
    ny = viewportH - n.h - (ny + n.h - viewportH) * RESTITUTION;
    vy = -vy * RESTITUTION;
  }

  const speed = Math.hypot(vx, vy);
  const done = speed < VEL_MIN_PX_PER_S;
  return { x: nx, y: ny, vx, vy, done };
}
